/**
 * Every dropdown, searchable picker and multi-filter in the app goes through here, so
 * behaviour and look stay identical — and identical to InfoCodes (s4ca-file-maker), whose
 * Select.jsx these three are taken from: MUI's Autocomplete for the searchable pickers and
 * MUI's Select for short fixed lists. react-select before that.
 *
 * The props are the ones the pages already pass, react-select's names included
 * (`isDisabled`, `isClearable`, `formatOptionLabel`), and anything else goes straight to
 * the Autocomplete. A picker is labelled by an outer <Field htmlFor> pointing at `inputId`;
 * with neither, its placeholder becomes its accessible name.
 *
 * Colours and sizes come from the MUI theme (lib/muiTheme.js), which is built from the
 * tokens in styles.css, so the controls follow light, dark and the palette like the rest.
 */
import { useEffect, useMemo, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Checkbox from '@mui/material/Checkbox'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import { api, useLookups } from '../lib/api'
import { modalityFits } from '../lib/modality'
import { promptFieldLabel } from '../lib/promptFields'
import { relabelText, usePartnerTerm, useCode } from '../lib/terms'
import { fundTypeLabel } from '../../shared/fundTypes.js'
import { useFieldDescription } from './fieldContext'

/**
 * Options may be grouped (`{ label, options: [...] }`). Autocomplete wants them flat with
 * a group name on each, so they are flattened once per options list.
 */
const flatten = (options) =>
  options.flatMap((o) => (o.options ? o.options.map((x) => ({ ...x, group: o.label })) : [o]))

const same = (a, b) => String(a?.value) === String(b?.value)

const labelOf = (o) => (o?.label === null || o?.label === undefined ? '' : String(o.label))

// react-select's per-option switch, which ModelSelect sets on a switched-off model.
const optionDisabled = (o) => !!o.isDisabled

/* `compact`: the 26px control that sits among .btn-sm buttons (the pager, the dashboard's
   month jump) rather than in a form. 12px type; the padding takes it to the buttons'
   height for both the plain select and the searchable one. */
const COMPACT = {
  '& .MuiInputBase-root': { fontSize: 'var(--fs-xs)' },
  // MUI's Select box carries no size class of its own, so it is reached through its root.
  '& .MuiInputBase-root .MuiSelect-select': { paddingTop: '4.5px', paddingBottom: '4.5px' },
  '& .MuiAutocomplete-inputRoot.MuiInputBase-sizeSmall': { paddingTop: '2px', paddingBottom: '2px' },
}

function useFlat(options) {
  return useMemo(() => {
    const flat = flatten(options ?? [])
    return { flat, grouped: flat.some((o) => o.group !== undefined) }
  }, [options])
}

/**
 * How a picker is named. A <Field htmlFor={inputId}> gives its label the id
 * `${inputId}-label` (components/ui.jsx), and that is the name; the placeholder, or an
 * explicit aria-label, is the fallback a browser uses when no such label is on the page.
 * `describedBy` is the hint or error the enclosing Field draws under the picker
 * (FieldContext, components/fieldContext.js).
 */
const nameProps = (inputId, ariaLabel, placeholder, describedBy) => ({
  'aria-labelledby': inputId ? `${inputId}-label` : undefined,
  'aria-label': ariaLabel ?? placeholder,
  'aria-describedby': describedBy,
})

/** The TextField both pickers draw, with its accessible name carried through to the input. */
const input = ({ placeholder, error, autoFocus, ariaLabel, inputId, describedBy }) =>
  function PickerInput(params) {
    return (
      <TextField
        {...params}
        placeholder={placeholder}
        error={error}
        autoFocus={autoFocus}
        slotProps={{
          ...params.slotProps,
          htmlInput: { ...params.slotProps.htmlInput, ...nameProps(inputId, ariaLabel, placeholder, describedBy) },
        }}
      />
    )
  }

/** Single-value searchable select. Reports the chosen option's `value`, or null. */
export const Picker = ({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  isClearable = true,
  isDisabled,
  formatOptionLabel,
  inputId,
  isLoading,
  error,
  autoFocus,
  compact,
  sx,
  'aria-label': ariaLabel,
  ...rest
}) => {
  const { describedBy } = useFieldDescription()
  const { flat, grouped } = useFlat(options)
  /* A value the list does not hold is shown as itself, as EnumSelect does: a filter that
     arrives in the address bar (Error Log's Model, from the AI usage report) is in force
     whether or not its facet is listed, and an empty box would say it was not. Not while
     the list is loading, when every value is missing and most are bare ids. */
  const { list, selected } = useMemo(() => {
    if (value === null || value === undefined || value === '') return { list: flat, selected: null }
    const hit = flat.find((o) => String(o.value) === String(value))
    if (hit || isLoading) return { list: flat, selected: hit ?? null }
    const stray = { value, label: String(value) }
    return { list: [...flat, stray], selected: stray }
  }, [flat, value, isLoading])

  return (
    <Autocomplete
      id={inputId}
      options={list}
      groupBy={grouped ? (o) => o.group ?? '' : undefined}
      value={selected}
      onChange={(_, opt) => onChange(opt ? opt.value : null)}
      getOptionLabel={labelOf}
      getOptionDisabled={optionDisabled}
      isOptionEqualToValue={same}
      disableClearable={!isClearable}
      disabled={isDisabled}
      loading={isLoading}
      noOptionsText="No matches"
      fullWidth
      renderOption={
        formatOptionLabel
          ? ({ key, ...props }, o) => (
              <li key={key} {...props}>
                {formatOptionLabel(o, { context: 'menu' })}
              </li>
            )
          : undefined
      }
      renderInput={input({ placeholder, error, autoFocus, ariaLabel, inputId, describedBy })}
      sx={compact ? [COMPACT, sx ?? {}] : sx}
      {...rest}
    />
  )
}

/** Multi-value searchable select; reports an array of values. */
export const MultiPicker = ({
  options,
  value = [],
  onChange,
  placeholder = 'Any',
  isDisabled,
  formatOptionLabel,
  inputId,
  isLoading,
  error,
  'aria-label': ariaLabel,
  ...rest
}) => {
  const { describedBy } = useFieldDescription()
  const { flat, grouped } = useFlat(options)
  const selected = useMemo(() => {
    const set = new Set((value || []).map(String))
    return flat.filter((o) => set.has(String(o.value)))
  }, [flat, value])

  return (
    <Autocomplete
      multiple
      id={inputId}
      disableCloseOnSelect
      options={flat}
      groupBy={grouped ? (o) => o.group ?? '' : undefined}
      value={selected}
      onChange={(_, opts) => onChange((opts || []).map((o) => o.value))}
      getOptionLabel={labelOf}
      getOptionDisabled={optionDisabled}
      isOptionEqualToValue={same}
      disabled={isDisabled}
      loading={isLoading}
      noOptionsText="No matches"
      fullWidth
      limitTags={3}
      renderOption={({ key, ...props }, o, { selected: on }) => (
        <li key={key} {...props}>
          <Checkbox checked={on} sx={{ mr: 1, p: 0.25 }} tabIndex={-1} />
          {formatOptionLabel ? formatOptionLabel(o, { context: 'menu' }) : labelOf(o)}
        </li>
      )}
      // The placeholder only while nothing is chosen; with chips in the box it is noise.
      renderInput={input({
        placeholder: selected.length ? undefined : placeholder,
        error,
        ariaLabel: ariaLabel ?? placeholder,
        inputId,
        describedBy,
      })}
      {...rest}
    />
  )
}

/**
 * Plain option-list select for small enum fields, where searching would be noise: MUI's
 * Select through TextField. Options may be strings. When clearable, the first item is the
 * placeholder and choosing it reports null.
 *
 * A list that comes from the data — every user, every address — is not an enum and wants
 * a Picker, which can be searched.
 */
export const EnumSelect = ({
  value,
  onChange,
  options,
  placeholder = 'Any',
  inputId,
  isClearable = true,
  isDisabled,
  disabled,
  error,
  compact,
  sx,
  'aria-label': ariaLabel,
}) => {
  const { describedBy } = useFieldDescription()
  const opts = useMemo(() => {
    const list = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o))
    /* A value the list does not hold — a filter arriving in the address bar before its
       facets have loaded — is shown as itself. MUI's Select would show an empty box for
       it, and an empty box reads as "no filter" while one is in force. */
    if (value !== null && value !== undefined && value !== '' && !list.some((o) => String(o.value) === String(value))) {
      list.push({ value, label: String(value) })
    }
    return list
  }, [options, value])
  return (
    <TextField
      select
      id={inputId}
      error={error}
      disabled={isDisabled ?? disabled}
      value={value ?? ''}
      onChange={(e) => {
        const v = e.target.value
        if (v === '') return onChange(null)
        // MUI hands back the string from the DOM; give the caller the option's own value.
        const hit = opts.find((o) => String(o.value) === String(v))
        onChange(hit ? hit.value : v)
      }}
      fullWidth
      sx={compact ? [COMPACT, sx ?? {}] : sx}
      slotProps={{
        // The focusable part of MUI's Select is its display div, so the name goes there.
        select: {
          displayEmpty: true,
          SelectDisplayProps: nameProps(inputId, ariaLabel, placeholder, describedBy),
          renderValue: (v) =>
            v === '' || v === null || v === undefined ? (
              <span className="enum-placeholder">{placeholder}</span>
            ) : (
              (opts.find((o) => String(o.value) === String(v))?.label ?? String(v))
            ),
        },
      }}
    >
      {isClearable && (
        <MenuItem value="">
          <em>{placeholder}</em>
        </MenuItem>
      )}
      {opts.map((o) => (
        <MenuItem key={String(o.value)} value={o.value} disabled={!!o.isDisabled}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  )
}

// ---------------------------------------------------------------- option renderers

/*
 * Alumni stay in the picker and are marked instead of removed. Dropping them would
 * take away the only way to name one, which is the whole point of being able to look
 * back — but leaving them unmarked is worse than either: every page now hides alumni
 * by default, so picking an unmarked one returns an empty list and looks like a bug.
 * The badge is the warning that the page's own alumni switch has to be on as well.
 */
/* `alumTitle` is passed in rather than read here: this is a module function, not a
   component, so it has no hook to ask what a partner unit is called — and reading the
   term from outside React would leave this one tooltip a frame behind the rest. */
const partnerOptionLabel = (o, meta, alumTitle) => (
  <span className="opt">
    <span className="opt-code">{o.code}</span>
    <span className="opt-name">{o.name}</span>
    {/* The kind, for the two kinds that are not the default. Marked rather than grouped:
        this picker is searched by typing a code far more often than it is browsed, and a
        grouped menu would put a training school's code under a heading the reader has to
        scroll to. A staff unit wears no badge — three quarters of the registry is staff,
        and a badge on every row is not a distinction. */}
    {o.fundType && o.fundType !== 'staff' && (
      <span className="badge" title={`A ${fundTypeLabel(o.fundType).toLowerCase()} rather than a partner unit`}>
        {o.fundType === 'training' ? 'school' : 'project'}
      </span>
    )}
    {o.status === 'alumni' && (
      <span className="badge" title={alumTitle}>
        alum
      </span>
    )}
    {meta?.context === 'menu' && (
      <span className="opt-meta">
        {o.flag ? `${o.flag} ` : ''}
        {o.countryName ?? ''}
        {o.newsletterCount !== undefined ? ` · ${o.newsletterCount}` : ''}
      </span>
    )}
  </span>
)

const countryOptionLabel = (o) => (
  <span className="opt">
    {o.flag && <span aria-hidden="true">{o.flag}</span>}
    <span className="opt-name">{o.label}</span>
  </span>
)

// ---------------------------------------------------------------- domain selects
// These read the shared /api/lookups payload so a page only has to say what it wants.

/**
 * Record picker with search across code and name.
 *
 * `withNewsletters` limits the list to records that actually have updates — used by the
 * report page, where the other 201 registry rows are noise.
 *
 * `fundType` limits it to one kind, or to several — a string or an array of them. Two
 * callers want opposite halves of the registry and both are right: "Updates by Partner
 * Unit" is a report about partner units and says so in its title, so a training school in
 * that picker is a record the report is not for; the update form's second picker is the
 * record an update is *also* about, which is the kinds its author may name (see
 * `secondaryKindsFor` in shared/fundTypes.js). The primary picker on that same form is left unset, because an
 * update can be filed against any of the three.
 */
/**
 * Which units match what has been typed, asked of the server.
 *
 * The picker filters the options it already holds, and a unit's label is a pair of
 * initials. The archive also knows an alias and a preferred name for some units, stored
 * to be searched and never shown (migration 0028) — so there is no way for this browser
 * to match "Gulnara" against a unit labelled "G" out of what it has. It asks instead, and
 * what comes back is a list of ids: enough to widen the filter, nothing to display.
 *
 * Debounced, because this is a keystroke handler talking to a rate-limited route. Ignores
 * anything shorter than two characters, one letter matching half the archive being no
 * help to anybody. And every reply is checked against the term still in the box before it
 * is used, so a slow answer to "Gul" cannot widen a filter that now reads "Gulnara".
 */
export function useHiddenMatches(input) {
  /* The answer is kept with the term it answers, and read back only while the two still
     agree. That is what makes a slow reply to "Gul" harmless once the box says "Gulnara" —
     and it means nothing has to be cleared when the term changes, so there is no
     setState left in the effect below. */
  const [matched, setMatched] = useState({ q: '', ids: null })
  const q = input.trim()

  useEffect(() => {
    // One letter matches half the archive, which is no help to anybody and a request per
    // keystroke to a rate-limited route.
    if (q.length < 2) return undefined
    const t = setTimeout(() => {
      api(`partners/find?q=${encodeURIComponent(q)}`)
        .then((r) => setMatched({ q, ids: new Set(r.ids ?? []) }))
        // A picker that still filters on what it can see is a working picker; there is
        // nothing here worth a red card over.
        .catch(() => {})
    }, 250)
    return () => clearTimeout(t)
  }, [q])

  return matched.q === q ? matched.ids : null
}

export function PartnerSelect({
  value,
  onChange,
  multi = false,
  withNewsletters = false,
  fundType = null,
  placeholder,
  inputId,
  isDisabled,
}) {
  const code = useCode()
  const term = usePartnerTerm()
  const { data, isLoading } = useLookups()
  const [typed, setTyped] = useState('')
  const hidden = useHiddenMatches(typed)
  const options = useMemo(() => {
    let list = data?.partners ?? []
    if (withNewsletters) list = list.filter((p) => p.newsletter_count > 0)
    /* `?? 'staff'` for a row from a lookups payload cached before the column existed —
       staff is what every record in this registry was until then, and the alternative is
       a picker that empties itself on a stale cache. */
    const kinds = fundType == null ? null : [].concat(fundType)
    if (kinds?.length) list = list.filter((p) => kinds.includes(p.fund_type ?? 'staff'))
    return list.map((p) => ({
      value: p.id,
      code: p.s4ca_code,
      name: p.display_name || p.s4ca_code,
      countryName: p.country_name,
      flag: p.flag_emoji,
      status: p.status,
      fundType: p.fund_type,
      newsletterCount: p.newsletter_count,
      /* What the box shows once one is chosen, and on its chip in a multi-picker. The
         search is wider than this (country and kind as well) and is done by `filter`
         below, so the label no longer has to carry the searchable text the way
         react-select's did. */
      label: p.display_name ? `${code(p.s4ca_code)} ${p.display_name}` : code(p.s4ca_code),
    }))
  }, [data, withNewsletters, fundType, code])

  /* Matches on code, name, country or kind — so typing "training" narrows a
     four-hundred-row picker to the training schools — or on what the server says matches
     something this browser cannot see. */
  const filter = (opts, { inputValue }) => {
    const q = inputValue.trim().toLowerCase()
    if (!q) return opts
    return opts.filter(
      (d) =>
        d.label.toLowerCase().includes(q) ||
        String(d.code).toLowerCase().includes(q) ||
        String(d.name).toLowerCase().includes(q) ||
        String(d.countryName ?? '').toLowerCase().includes(q) ||
        fundTypeLabel(d.fundType).toLowerCase().includes(q) ||
        hidden?.has(d.value) === true
    )
  }

  const Comp = multi ? MultiPicker : Picker
  return (
    <Comp
      inputId={inputId}
      options={options}
      value={value}
      onChange={onChange}
      isLoading={isLoading}
      isDisabled={isDisabled}
      formatOptionLabel={(o, meta) =>
        partnerOptionLabel(
          o,
          meta,
          relabelText('A past partner unit — pages hide these unless asked', term)
        )
      }
      placeholder={relabelText(placeholder ?? (multi ? 'Any partner unit' : 'Search by code or name…'), term)}
      // Only what was typed. Autocomplete also reports the chosen unit's label being put
      // in the box ('reset'), which is not a search and should not reach the server.
      onInputChange={(_, v, reason) => setTyped(reason === 'input' ? v : '')}
      filterOptions={filter}
    />
  )
}

/** Country picker, grouped so the organisational buckets sit apart from real countries. */
export function CountrySelect({ value, onChange, multi = false, placeholder, inputId, grouped = true, isDisabled }) {
  const { data, isLoading } = useLookups()
  const options = useMemo(() => {
    const list = (data?.countries ?? []).map((c) => ({
      value: c.id,
      label: c.name,
      flag: c.flag_emoji,
      kind: c.kind,
    }))
    if (!grouped) return list
    const real = list.filter((c) => c.kind === 'country')
    const other = list.filter((c) => c.kind !== 'country')
    const groups = []
    if (real.length) groups.push({ label: 'Countries', options: real })
    if (other.length) groups.push({ label: 'Other groupings', options: other })
    return groups
  }, [data, grouped])

  const Comp = multi ? MultiPicker : Picker
  return (
    <Comp
      inputId={inputId}
      options={options}
      value={value}
      onChange={onChange}
      isLoading={isLoading}
      isDisabled={isDisabled}
      formatOptionLabel={countryOptionLabel}
      placeholder={placeholder ?? (multi ? 'Any country' : 'Select a country…')}
    />
  )
}

/**
 * Prompt picker.
 *
 * `targetField` decides the order, not the contents: the prompts written for this
 * field come first, and every other active prompt follows under its own heading.
 * It used to be a filter, which read as tidy and behaved as a wall — a prompt for
 * reading text out of a picture was invisible to the file importer unless it had
 * been created against exactly the right field, and there was no way to retarget
 * it from the picker. Nothing on the server cares: an instruction is an
 * instruction, whichever field it was filed under.
 *
 * Within each of those two groups the order is the priority set on AI Settings → Prompts
 * (`sort_order`), so the prompt an admin put at the top of a field's list is the
 * first one an editor is offered for that field — here, on the update form, in the
 * file importer and in the studio alike. The lookups query sorts on it already;
 * sorting again here is what keeps that true if the payload order ever changes,
 * and it is the only ordering rule the "other prompts" group has — those come from
 * several fields at once, so they are kept in their own field's order rather than
 * interleaved by rank.
 *
 * `onlyTargetField` restores the strict behaviour for the settings page, where the
 * point is to name the default prompt *of* a field.
 */
const byPriority = (a, b) =>
  (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.name).localeCompare(String(b.name))

const byFieldThenPriority = (a, b) =>
  promptFieldLabel(a.target_field).localeCompare(promptFieldLabel(b.target_field)) ||
  byPriority(a, b)

export function PromptSelect({
  value,
  onChange,
  targetField,
  onlyTargetField = false,
  placeholder = 'Choose a prompt…',
  inputId,
}) {
  const { data, isLoading } = useLookups()
  const options = useMemo(() => {
    const opt = (p) => ({
      value: p.id,
      label: p.name,
      mode: p.mode,
      description: p.description,
      targetField: p.target_field,
      fieldLabel: promptFieldLabel(p.target_field),
      rank: null,
    })
    // Rank counts within a field, not down the list: a group that draws on several
    // fields at once has a first prompt for each of them and no first prompt of
    // its own, so numbering it 1, 2, 3 would say something untrue.
    const ranked = (rows) => {
      const seen = new Map()
      return rows.map((p) => {
        const n = (seen.get(p.target_field) ?? 0) + 1
        seen.set(p.target_field, n)
        return { ...opt(p), rank: n }
      })
    }
    const list = data?.prompts ?? []
    if (!targetField) return ranked([...list].sort(byFieldThenPriority))

    const mine = ranked(list.filter((p) => p.target_field === targetField).sort(byPriority))
    if (onlyTargetField) return mine

    // No rank badge here: these are ordered by the field they belong to, and a row
    // of "top" markers on prompts written for somewhere else is noise.
    const others = list
      .filter((p) => p.target_field !== targetField)
      .sort(byFieldThenPriority)
      .map(opt)
    if (!others.length) return mine
    if (!mine.length) return others
    return [
      { label: 'Written for this', options: mine },
      { label: 'Your other prompts', options: others },
    ]
  }, [data, targetField, onlyTargetField])
  return (
    <Picker
      inputId={inputId}
      options={options}
      value={value}
      onChange={onChange}
      isLoading={isLoading}
      placeholder={placeholder}
      formatOptionLabel={(o, meta) => (
        <span className="opt">
          <span className="opt-name">{o.label}</span>
          {o.rank === 1 && (
            <span className="badge" title="First choice for this field — set on AI Settings → Prompts">
              top
            </span>
          )}
          {meta?.context === 'menu' && (
            <span className="opt-meta">
              {o.mode}
              {o.targetField !== targetField ? ` · for ${o.fieldLabel.toLowerCase()}` : ''}
            </span>
          )}
        </span>
      )}
    />
  )
}

/**
 * Model picker restricted to a modality, so a text field can never be pointed at
 * an image generation model. A multimodal model counts as both text and vision,
 * which is what most bring-your-own models are. Disabled models stay visible but
 * unselectable.
 */
export function ModelSelect({ value, onChange, modality, placeholder = 'Use the mapped default', inputId }) {
  const { data, isLoading } = useLookups()
  const options = useMemo(() => {
    let list = data?.models ?? []
    if (modality) list = list.filter((m) => modalityFits(m.modality, modality))
    const byProvider = new Map()
    for (const m of list) {
      if (!byProvider.has(m.provider_name)) byProvider.set(m.provider_name, [])
      byProvider.get(m.provider_name).push({
        value: m.id,
        label: m.label,
        modality: m.modality,
        modelKey: m.model_key,
        isDisabled: !m.is_enabled || !m.provider_enabled,
      })
    }
    return [...byProvider.entries()].map(([label, opts]) => ({ label, options: opts }))
  }, [data, modality])
  return (
    <Picker
      inputId={inputId}
      options={options}
      value={value}
      onChange={onChange}
      isLoading={isLoading}
      placeholder={placeholder}
      formatOptionLabel={(o, meta) => (
        <span className="opt">
          <span className="opt-name">{o.label}</span>
          {meta?.context === 'menu' && (
            <span className="opt-meta">{o.isDisabled ? 'disabled' : o.modality}</span>
          )}
        </span>
      )}
    />
  )
}

/**
 * Every year the archive holds, or just the ones passed in `years` — a partner unit's
 * own years, say, where offering the other fifteen would be offering fifteen empty
 * answers. Years are strings throughout, the way `substr(published_on, 1, 4)` returns
 * them, so a caller with numbers should hand over strings.
 */
export function YearSelect({ value, onChange, inputId, placeholder = 'Any year', years }) {
  const { data } = useLookups()
  const options = useMemo(
    () => (years ?? data?.years ?? []).map((y) => ({ value: y, label: y })),
    [years, data]
  )
  return <Picker inputId={inputId} options={options} value={value} onChange={onChange} placeholder={placeholder} />
}
